/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LocaleMessages } from "@epic-mw/i18n";

interface Props {
	messages?: LocaleMessages;
	structuredData?: {
		website?: string;
		organization?: string;
		softwareApplication?: string;
	};
	/**
	 * Optional brand subdirectory under `/cosmos/static/favicons/`. Per-tenant
	 * sites that ship their own favicon set drop the assets at
	 * `/cosmos/static/favicons/{brand}/...` and pass `brand` here. Tenants
	 * without an override leave it unset and inherit the default Epic Games
	 * favicons at `/cosmos/static/favicons/...`. Drop-in pattern so adding a
	 * new branded favicon set is two changes: copy assets into a new subdir +
	 * set `faviconBrand` on the site config.
	 */
	brand?: string;
	basePath?: string;
}

export const FavIcons = ({ messages = {}, structuredData, brand, basePath = "" }: Props): React.JSX.Element => {
	const title = messages["epic.app.meta.title"];
	const staticPrefix = `${basePath}/cosmos/static`;
	const faviconsBase = brand ? `${staticPrefix}/favicons/${brand}` : `${staticPrefix}/favicons`;

	return (
		<>
			<link rel="icon" type="image/svg+xml" href={`${faviconsBase}/favicon.svg`} />
			<link rel="icon" type="image/png" href={`${faviconsBase}/favicon-96x96.png`} sizes="96x96" />
			<link rel="apple-touch-icon" sizes="180x180" href={`${faviconsBase}/apple-touch-icon.png`} />
			<link rel="manifest" href={`${faviconsBase}/site.webmanifest`} />
			<meta name="apple-mobile-web-app-title" content={title} />
			<meta name="application-name" content={title} />
			<meta name="msapplication-TileColor" content="#26bbff" />
			<meta name="theme-color" content="#ffffff" />
			<link rel="alternate" type="application/rss+xml" title={title} href="/rss" />
			{structuredData && structuredData.website ? (
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: structuredData.website,
					}}
				/>
			) : null}
			{structuredData && structuredData.organization ? (
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: structuredData.organization,
					}}
				/>
			) : null}
			{structuredData && structuredData.softwareApplication ? (
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: structuredData.softwareApplication,
					}}
				/>
			) : null}
		</>
	);
};
